import type { RecordAny, SearchParams, SearchResponse } from 'meilisearch'

/**
 * Keeps in `_formatted` only the attributes the request asked to highlight or crop.
 * Meilisearch puts every retrieved attribute there, so each hit carried a full second
 * copy of the document into the SSR payload.
 */
const trimFormatted = <T extends RecordAny>(response: SearchResponse<T>, params?: SearchParams) => {
  const attributes = [...(params?.attributesToHighlight ?? []), ...(params?.attributesToCrop ?? [])]
  if (!attributes.length || attributes.includes('*')) return response

  // Nested attributes ("organization.title") live under their top-level key
  const keys = new Set(attributes.map((attribute) => attribute.split('.')[0]))

  for (const hit of response.hits) {
    const formatted = hit._formatted as RecordAny | undefined
    if (!formatted) continue
    hit._formatted = Object.fromEntries(
      Object.entries(formatted).filter(([key]) => keys.has(key))
    ) as typeof hit._formatted
  }

  return response
}

/**
 * Search in a Meilisearch index.
 *
 * Replaces useMeiliSearch from nuxt-meilisearch, which also stores every response in
 * useState: the results then reached the SSR payload twice, next to the useAsyncData
 * copy the widgets actually read (#296).
 */
export const useMeiliIndexSearch = (index: string) => {
  const client = useMeiliSearchRef()

  const search = async <T extends RecordAny = RecordAny>(query: string, params?: SearchParams) =>
    trimFormatted(await client.index(index).search<T>(query, params), params)

  return { search }
}
